import React, { useState, useEffect } from 'react';
import {
  FiPlus, FiEdit, FiTrash2, FiSearch, FiEye, FiEyeOff, FiArrowUp, FiArrowDown,
  FiHelpCircle, FiDollarSign, FiCreditCard, FiUser, FiTool, FiMoreHorizontal,
  FiCheck, FiX, FiChevronDown, FiChevronUp, FiBookOpen
} from 'react-icons/fi';
import { Button } from '../../components/Button';
import { API_URL } from '../../api/client';

interface FAQ {
  faq_id: string;
  category: string;
  question: string;
  answer: string;
  display_order: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

interface ToastMessage {
  id: number;
  type: 'success' | 'error' | 'info';
  message: string;
}

const FAQs: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [publishedFilter, setPublishedFilter] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [expandedFaqs, setExpandedFaqs] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState({
    category: '',
    question: '',
    answer: '',
    display_order: 0,
    is_published: true,
  });

  const categories = [
    'Getting Started',
    'Loan Process',
    'Repayment',
    'Account',
    'Technical',
    'Other',
  ];

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Getting Started':
        return <FiHelpCircle size={20} />;
      case 'Loan Process':
        return <FiDollarSign size={20} />;
      case 'Repayment':
        return <FiCreditCard size={20} />;
      case 'Account':
        return <FiUser size={20} />;
      case 'Technical':
        return <FiTool size={20} />;
      default:
        return <FiMoreHorizontal size={20} />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Getting Started':
        return { start: '#10b981', end: '#059669' }; // Green
      case 'Loan Process':
        return { start: '#3b82f6', end: '#2563eb' }; // Blue
      case 'Repayment':
        return { start: '#8b5cf6', end: '#7c3aed' }; // Purple
      case 'Account':
        return { start: '#f59e0b', end: '#d97706' }; // Amber
      case 'Technical':
        return { start: '#6366f1', end: '#4f46e5' }; // Indigo
      default:
        return { start: '#64748b', end: '#475569' }; // Slate
    }
  };

  const showToast = (type: 'success' | 'error' | 'info', message: string) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };

  const toggleFaqExpansion = (faqId: string) => {
    setExpandedFaqs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(faqId)) {
        newSet.delete(faqId);
      } else {
        newSet.add(faqId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    fetchFAQs();
  }, [categoryFilter, publishedFilter, searchQuery]);

  const fetchFAQs = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category', categoryFilter);
      if (publishedFilter !== 'all') params.append('is_published', publishedFilter);
      if (searchQuery) params.append('search', searchQuery);

      const response = await fetch(
        `${API_URL}/admin/faqs?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();
      if (result.success) {
        setFaqs(result.data);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = editingFaq
        ? `${API_URL}/admin/faqs/${editingFaq.faq_id}`
        : `${API_URL}/admin/faqs`;

      const response = await fetch(url, {
        method: editingFaq ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      if (result.success) {
        showToast('success', `FAQ ${editingFaq ? 'updated' : 'created'} successfully!`);
        setShowModal(false);
        resetForm();
        fetchFAQs();
      } else {
        showToast('error', result.message || 'Failed to save FAQ');
      }
    } catch (error) {
      console.error('Error saving FAQ:', error);
      showToast('error', 'An error occurred while saving the FAQ');
    }
  };

  const handleDelete = async (faqId: string) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/faqs/${faqId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (result.success) {
        showToast('success', 'FAQ deleted successfully!');
        fetchFAQs();
      } else {
        showToast('error', result.message || 'Failed to delete FAQ');
      }
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      showToast('error', 'An error occurred while deleting the FAQ');
    }
  };

  const handleTogglePublished = async (faq: FAQ) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/faqs/${faq.faq_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_published: !faq.is_published }),
      });

      const result = await response.json();
      if (result.success) {
        fetchFAQs();
      }
    } catch (error) {
      console.error('Error toggling FAQ:', error);
    }
  };

  const handleMoveOrder = async (faq: FAQ, direction: 'up' | 'down') => {
    // Find FAQs in the same category
    const categoryFaqs = faqs
      .filter((f) => f.category === faq.category)
      .sort((a, b) => a.display_order - b.display_order);

    const currentIndex = categoryFaqs.findIndex((f) => f.faq_id === faq.faq_id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === categoryFaqs.length - 1)
    ) {
      return; // Can't move
    }

    const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const currentFaq = categoryFaqs[currentIndex];
    const swapFaq = categoryFaqs[swapIndex];

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/admin/faqs/bulk/display-orders`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          faqs: [
            { faq_id: currentFaq.faq_id, display_order: swapFaq.display_order },
            { faq_id: swapFaq.faq_id, display_order: currentFaq.display_order },
          ],
        }),
      });

      fetchFAQs();
    } catch (error) {
      console.error('Error reordering FAQs:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      category: '',
      question: '',
      answer: '',
      display_order: 0,
      is_published: true,
    });
    setEditingFaq(null);
  };

  const openEditModal = (faq: FAQ) => {
    setEditingFaq(faq);
    setFormData({
      category: faq.category,
      question: faq.question,
      answer: faq.answer,
      display_order: faq.display_order,
      is_published: faq.is_published,
    });
    setShowModal(true);
  };

  // Group FAQs by category
  const groupedFaqs = faqs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {} as Record<string, FAQ[]>);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%)', padding: '2rem' }}>
      {/* Toast Notifications */}
      <div style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            style={{
              background: toast.type === 'success' ? '#10b981' : toast.type === 'error' ? '#ef4444' : '#3b82f6',
              color: 'white',
              padding: '1rem 1.5rem',
              borderRadius: '0.75rem',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              minWidth: '300px',
              animation: 'slideInRight 0.3s ease-out',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}
          >
            {toast.type === 'success' && <FiCheck size={20} />}
            {toast.type === 'error' && <FiX size={20} />}
            {toast.type === 'info' && <FiBookOpen size={20} />}
            <span style={{ flex: 1, fontWeight: 500 }}>{toast.message}</span>
          </div>
        ))}
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#1f2937', margin: 0, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <FiBookOpen size={32} style={{ color: '#3b82f6' }} />
            FAQs Management
          </h1>
          <p style={{ color: '#6b7280', marginTop: '0.5rem', marginBottom: 0 }}>
            Manage your frequently asked questions
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          variant="primary"
          size="md"
        >
          <FiPlus size={20} /> Create FAQ
        </Button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        <div style={{ background: 'white', padding: '1.5rem', borderRadius: '1rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', transition: 'all 0.3s ease' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: 600 }}>Total FAQs</span>
            <FiBookOpen style={{ color: '#3b82f6' }} size={20} />
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1f2937' }}>{faqs.length}</div>
        </div>
        <div style={{ background: 'white', padding: '1.5rem', borderRadius: '1rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', transition: 'all 0.3s ease' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: 600 }}>Published</span>
            <FiEye style={{ color: '#10b981' }} size={20} />
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1f2937' }}>{faqs.filter(f => f.is_published).length}</div>
        </div>
        <div style={{ background: 'white', padding: '1.5rem', borderRadius: '1rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', transition: 'all 0.3s ease' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: 600 }}>Draft</span>
            <FiEyeOff style={{ color: '#f59e0b' }} size={20} />
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1f2937' }}>{faqs.filter(f => !f.is_published).length}</div>
        </div>
        <div style={{ background: 'white', padding: '1.5rem', borderRadius: '1rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', transition: 'all 0.3s ease' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: 600 }}>Categories</span>
            <FiMoreHorizontal style={{ color: '#8b5cf6' }} size={20} />
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1f2937' }}>{Object.keys(groupedFaqs).length}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background: 'white', padding: '1.5rem', borderRadius: '1rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', marginBottom: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '250px', position: 'relative' }}>
          <FiSearch style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} size={18} />
          <input
            type="text"
            placeholder="Search FAQs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              paddingLeft: '2.5rem',
              paddingRight: '1rem',
              paddingTop: '0.625rem',
              paddingBottom: '0.625rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              outline: 'none',
              transition: 'all 0.2s'
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{
            padding: '0.625rem 1rem',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            backgroundColor: 'white',
            cursor: 'pointer',
            outline: 'none'
          }}
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <select
          value={publishedFilter}
          onChange={(e) => setPublishedFilter(e.target.value)}
          style={{
            padding: '0.625rem 1rem',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            backgroundColor: 'white',
            cursor: 'pointer',
            outline: 'none'
          }}
        >
          <option value="all">All Status</option>
          <option value="true">Published</option>
          <option value="false">Draft</option>
        </select>
      </div>

      {/* FAQs by Category */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => {
          const colors = getCategoryColor(category);
          return (
            <div key={category} style={{ background: 'white', borderRadius: '1rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
              {/* Category Header */}
              <div style={{
                background: `linear-gradient(135deg, ${colors.start} 0%, ${colors.end} 100%)`,
                padding: '1.25rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <div style={{ background: 'rgba(255, 255, 255, 0.2)', padding: '0.5rem', borderRadius: '0.5rem', color: 'white' }}>
                  {getCategoryIcon(category)}
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'white', margin: 0 }}>{category}</h2>
                  <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.9)', margin: 0 }}>
                    {categoryFaqs.length} FAQ{categoryFaqs.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {/* FAQ Items */}
              <div>
                {categoryFaqs
                  .sort((a, b) => a.display_order - b.display_order)
                  .map((faq, index) => {
                    const isExpanded = expandedFaqs.has(faq.faq_id);
                    return (
                      <div key={faq.faq_id} style={{ borderBottom: index < categoryFaqs.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                        <div style={{ padding: '1.25rem 1.5rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                          {/* Order Controls */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', paddingTop: '0.25rem' }}>
                            <Button
                              onClick={() => handleMoveOrder(faq, 'up')}
                              disabled={index === 0}
                              variant="ghost"
                              size="sm"
                              title="Move up"
                            >
                              <FiArrowUp size={16} />
                            </Button>
                            <Button
                              onClick={() => handleMoveOrder(faq, 'down')}
                              disabled={index === categoryFaqs.length - 1}
                              variant="ghost"
                              size="sm"
                              title="Move down"
                            >
                              <FiArrowDown size={16} />
                            </Button>
                          </div>

                          {/* Content */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              onClick={() => toggleFaqExpansion(faq.faq_id)}
                              style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer', marginBottom: isExpanded ? '0.75rem' : 0 }}
                            >
                              <div style={{ paddingTop: '0.125rem' }}>
                                {isExpanded ? <FiChevronUp size={20} style={{ color: colors.start }} /> : <FiChevronDown size={20} style={{ color: '#9ca3af' }} />}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.5rem' }}>
                                  <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937', margin: 0 }}>{faq.question}</h3>
                                  <span style={{
                                    padding: '0.25rem 0.75rem',
                                    fontSize: '0.75rem',
                                    borderRadius: '9999px',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    background: faq.is_published ? '#d1fae5' : '#f3f4f6',
                                    color: faq.is_published ? '#065f46' : '#4b5563'
                                  }}>
                                    {faq.is_published ? 'Published' : 'Draft'}
                                  </span>
                                </div>
                                {isExpanded && (
                                  <p style={{ fontSize: '0.875rem', color: '#6b7280', whiteSpace: 'pre-wrap', margin: 0, lineHeight: '1.5' }}>
                                    {faq.answer}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div style={{ display: 'flex', gap: '0.5rem', paddingTop: '0.125rem' }}>
                            <Button
                              onClick={() => handleTogglePublished(faq)}
                              variant="secondary"
                              size="sm"
                              title={faq.is_published ? 'Unpublish' : 'Publish'}
                            >
                              {faq.is_published ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                            </Button>
                            <Button
                              onClick={() => openEditModal(faq)}
                              variant="secondary"
                              size="sm"
                              title="Edit"
                            >
                              <FiEdit size={16} />
                            </Button>
                            <Button
                              onClick={() => handleDelete(faq.faq_id)}
                              variant="danger"
                              size="sm"
                              title="Delete"
                            >
                              <FiTrash2 size={16} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '1rem',
            maxWidth: '42rem',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', padding: '0.75rem', borderRadius: '0.75rem', color: 'white' }}>
                  <FiBookOpen size={24} />
                </div>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', margin: 0 }}>
                  {editingFaq ? 'Edit FAQ' : 'Create FAQ'}
                </h2>
              </div>
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        outline: 'none'
                      }}
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                      Question *
                    </label>
                    <textarea
                      value={formData.question}
                      onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        outline: 'none',
                        fontFamily: 'inherit',
                        resize: 'vertical'
                      }}
                      rows={2}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                      Answer *
                    </label>
                    <textarea
                      value={formData.answer}
                      onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        outline: 'none',
                        fontFamily: 'inherit',
                        resize: 'vertical'
                      }}
                      rows={6}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                      Display Order
                    </label>
                    <input
                      type="number"
                      value={formData.display_order}
                      onChange={(e) =>
                        setFormData({ ...formData, display_order: parseInt(e.target.value) })
                      }
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        outline: 'none'
                      }}
                      min="0"
                    />
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', marginBottom: 0 }}>
                      Lower numbers appear first in the category
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      id="is_published"
                      checked={formData.is_published}
                      onChange={(e) =>
                        setFormData({ ...formData, is_published: e.target.checked })
                      }
                      style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
                    />
                    <label htmlFor="is_published" style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
                      Published
                    </label>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
                  <Button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    variant="secondary"
                    size="md"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                  >
                    {editingFaq ? 'Update' : 'Create'} FAQ
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQs;
